import './FundItem.sass';
import './ETFPCF.sass';
import { useArticles } from '../api';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useEffect, useMemo, useState, useRef } from 'react';
import { useStockList, useETFPCF } from '../api';
import Breadcrumbs from '../components/Breadcrumbs';
import Hero from '../components/Hero';
import { useLocation } from 'react-router';
import { filter, find } from 'lodash';
import Loading from './Loading';
import Meta from '../components/Meta';
import iconDownload from '../assets/common/file-download.svg';
import getTimestamp from '../library/tools/getTimestamp';
import moment from 'moment';
import NoData from '../components/NoData';
import { useWindowSize } from 'react-use';

import tw from 'date-fns/locale/zh-TW';
registerLocale('zh-TW', tw);

const ETFPCF = (props) => {
	const { routeData } = props;
	const { breadcrumbs } = useArticles(routeData.guid, routeData.route);
	const stockList = useStockList()?.list;
	const location = useLocation();
	const [fundChanged, setFundChaged] = useState(false);
	const stocksSection = useRef(null);
	const { width } = useWindowSize();

	const isSales = useMemo(() => {
		return location.pathname.includes('_sales');
	}, [location]);

	const targetFundID = useMemo(() => {
		return new URLSearchParams(location.search).get('fundID');
	}, [location]);

	const etfList = useMemo(() => {
		if (stockList.length)
			return filter(stockList, (item) => {
				return item.ec001 === 3;
			});
		return [];
	}, [stockList]);

	const [currentStock, setCurrentStock] = useState(null);

	useEffect(() => {
		if (etfList?.[0]) {
			if (targetFundID) {
				const stock = filter(etfList, (item) => {
					return item?.result?.[0].fundID === targetFundID;
				});
				setCurrentStock(stock[0]);
			} else {
				setCurrentStock(etfList[0]);
			}
		}
	}, [etfList, targetFundID]);

	const fundID = useMemo(() => {
		if (!fundChanged && targetFundID) return targetFundID;
		if (currentStock) return currentStock.result?.[0]?.fundID;
		return null;
	}, [currentStock, fundChanged, targetFundID]);

	const [queryDate, setQueryDate] = useState('');

	const { etfpcf, isLoading, isError } = useETFPCF(fundID, queryDate ? moment(queryDate).format('YYYY/MM/DD') : '');

	const stocks = useMemo(() => {
		if (etfpcf && etfpcf.result) {
			return filter(etfpcf.result, (item) => {
				return item.kind === 'stock';
			});
		}
		return [];
	}, [etfpcf]);

	const bonds = useMemo(() => {
		if (etfpcf && etfpcf.result) {
			return filter(etfpcf.result, (item) => {
				return item.kind === 'bond';
			});
		}
		return [];
	}, [etfpcf]);

	const futures = useMemo(() => {
		if (etfpcf && etfpcf.result) {
			return filter(etfpcf.result, (item) => {
				return item.kind === 'futures';
			});
		}
		return [];
	}, [etfpcf]);

	const others = useMemo(() => {
		if (etfpcf && etfpcf.result) {
			return filter(etfpcf.result, (item) => {
				return item.kind === 'other';
			});
		}
		return [];
	}, [etfpcf]);

	const maxDate = useMemo(() => {
		if (currentStock && currentStock.result && currentStock.result.length && currentStock.result[0].announce) {
			return moment(currentStock.result[0].announce).format('YYYY/MM/DD');
		}
		return moment().format('YYYY/MM/DD');
	}, [currentStock]);

	useEffect(() => {
		if (etfpcf && etfpcf.announce) {
			const pcfDate = moment(getTimestamp(etfpcf.announce)).format('YYYY/MM/DD');

			if (queryDate) {
				const qDate = moment(queryDate).format('YYYY/MM/DD');

				if (pcfDate !== qDate) {
					alert('無此PCF資料!!');
					setQueryDate(new Date(pcfDate));
				}
			}
		}
	}, [etfpcf, queryDate]);

	const [scrolled, setScrolled] = useState(false);

	useEffect(() => {
		setTimeout(() => {
			const scrollSection = stocksSection.current;

			if (scrollSection && etfpcf && targetFundID && !scrolled) {

				let yOffset = 0;

				if (width >= 1024) yOffset = -150;
				if (width < 1024 && width >= 768) yOffset = -70;
				if (width < 768) yOffset = (width / 100 * 16 * -1) - 10;

				const y = scrollSection.getBoundingClientRect().top + window.pageYOffset + yOffset;

				window.scrollTo({ top: y, behavior: 'smooth' });

				setScrolled(true);
			}
		}, 500);
	}, [etfpcf, targetFundID, width, scrolled]);

	const downloadLink = useMemo(() => {
		if (etfpcf && etfpcf.announce) {
			const pcfDate = moment(getTimestamp(etfpcf.announce)).format('YYYYMMDD');
			return `/api/WebSite/pcfexcel/${fundID}/${pcfDate}`;
		}

		return '';
	}, [etfpcf, fundID]);

	const dataDate = etfpcf?.datadate ? moment(getTimestamp(etfpcf.datadate)).format('YYYY/MM/DD') : '';

	const isError400 = useMemo(() => {
		if (isError?.responseStatus?.message === '股票代號錯誤') return true;
		if (isError?.responseStatus?.message === '無資料') return true;
		if (isError?.responseStatus?.message === '請設定ETF境內外') return true;
		if (isError?.responseStatus?.message === '尚未到掛牌日') return true;
		if (isError?.responseStatus?.message === '掛牌日後才有資料') return true;
		if (isError?.responseStatus?.message === '未設定掛牌日') return true;
		return false;
	}, [isError]);

	const isError500 = useMemo(() => {
		if (isError) return true;
		return false;
	}, [isError]);

	if (isError400) return (
		<div className='FundItem' id='ETFPCF'>
			<Meta data={routeData} />
			{isSales ? null : <Hero data={breadcrumbs} />}
			<Breadcrumbs data={breadcrumbs} />

			<div className='common-pad-wrap'>
				{etfList.length ? (
					<div className='search-box'>
						<div className='group'>
							<div className='group-title'>基金類型</div>

							<select
								className='category'
								value={currentStock?.guid}
								onChange={(event) => {
									const { value } = event.target;

									setCurrentStock(
										find(etfList, (item) => {
											return item.guid === value;
										})
									);

									setFundChaged(true);
								}}>
								{etfList.map((stock, index) => {
									return (
										<option key={stock.guid + index} value={stock.guid}>
											{stock.twName}
										</option>
									);
								})}
							</select>
						</div>

						<div className='group'>
							<div className='group-title'>查詢日期</div>
							<DatePicker locale='zh-TW' selected={queryDate} onChange={setQueryDate} dateFormat='yyyy/MM/dd' allowSameDay={true} placeholderText='YYYY/MM/DD' maxDate={new Date(maxDate)} />
						</div>
					</div>
				) : null}
			</div>

			<NoData />
		</div>
	)

	return (
		<div className='FundItem' id='ETFPCF'>
			<Meta data={routeData} />
			{isSales ? null : <Hero data={breadcrumbs} />}
			<Breadcrumbs data={breadcrumbs} />

			<div className='common-pad-wrap'>
				{etfList.length ? (
					<div className='search-box'>
						<div className='group'>
							<div className='group-title'>基金類型</div>

							<select
								className='category'
								value={currentStock?.guid}
								onChange={(event) => {
									const { value } = event.target;

									setCurrentStock(
										find(etfList, (item) => {
											return item.guid === value;
										})
									);

									setFundChaged(true);
								}}>
								{etfList.map((stock) => {
									return (
										<option key={stock.guid} value={stock.guid}>
											{stock.twName}
										</option>
									);
								})}
							</select>
						</div>

						<div className='group'>
							<div className='group-title'>查詢日期</div>
							<DatePicker locale='zh-TW' selected={queryDate} onChange={setQueryDate} dateFormat='yyyy/MM/dd' allowSameDay={true} placeholderText='YYYY/MM/DD' maxDate={new Date(maxDate)} />
						</div>
					</div>
				) : null}

				{isLoading || isError500 || !currentStock || !etfpcf ? (
					<Loading />
				) : (
					<div className='main-content'>
						<div className='flex-top'>
							<div className='left'>
								<h1>{currentStock.twName}</h1>
								<div className='sub'>{currentStock.result[0].etf002}</div>
							</div>

							<div className='right'>{currentStock.ec314 === 1 ? <div className='hot'>hot</div> : null}</div>

							<div className='download-wrap'>
								<a href={downloadLink}>
									<div className='icon'>
										<img src={iconDownload} alt='' />
									</div>

									<div className='text'>檔案下載</div>
								</a>
							</div>
						</div>

						<div className='section-title'>
							<div className='text'>{moment(getTimestamp(etfpcf.announce)).format('YYYY/MM/DD')} 申購買回清單</div>
							<div className='line'></div>
						</div>

						<div className='flex-table'>
							<div className='item-group'>
								<div className='head'>每申購基數之預收申購總價金(元)</div>
								<div className='info'>{etfpcf.basketValue}</div>
							</div>

							<div className='item-group'>
								<div className='head'>{dataDate} 基金淨資產價值(元)</div>
								<div className='info'>{etfpcf.totalAV}</div>
							</div>

							<div className='item-group'>
								<div className='head'>{dataDate} 已發行受益權單位總數</div>
								<div className='info'>{etfpcf.totalIssues}</div>
							</div>

							<div className='item-group'>
								<div className='head'>與前日已發行單位差異數</div>
								<div className='info'>{etfpcf.issuesDiff}</div>
							</div>

							<div className='item-group'>
								<div className='head'>{dataDate} 每受益權單位淨資產價值(元)</div>
								<div className='info'>{etfpcf.nav}</div>
							</div>

							<div className='item-group'>
								<div className='head'>每申購/買回基數之受益權單位數</div>
								<div className='info'>{etfpcf.baseValue}</div>
							</div>

							<div className='item-group'>
								<div className='head'>每申購/買回基數約當市值(元)</div>
								<div className='info'>{etfpcf.estcValue}</div>
							</div>

							<div className='item-group'>
								<div className='head'>{dataDate} 每基數實際申購總價金(元)</div>
								<div className='info'>{etfpcf.basketValueP}</div>
							</div>

							<div className='item-group'>
								<div className='head'>{dataDate} 每基數申購總價金差異額(元)</div>
								<div className='info'>{etfpcf.diffBasketValue}</div>
							</div>

							{etfpcf.totalIssuesT01 ? (
								<div className='item-group'>
									<div className='head'>{moment(getTimestamp(etfpcf.publish)).format('YYYY/MM/DD')} 預估發行受益權單位總數</div>
									<div className='info'>{etfpcf.totalIssuesT01}</div>
								</div>
							) : null}
						</div>

						<div className='section-title has-date'>
							<div className='text'>基金資產</div>
							<div className='line'></div>
							<div className='data-date'>資料日期 : {dataDate}</div>
						</div>

						<div className='flex-table'>
							<div className='item-group'>
								<div className='head'>基金淨資產價值(元)</div>
								<div className='info'>{etfpcf.totalAV}</div>
							</div>

							<div className='item-group'>
								<div className='head'>基金在外流通單位數</div>
								<div className='info'>{etfpcf.totalIssues}</div>
							</div>

							<div className='item-group'>
								<div className='head'>基金每單位淨值(元)</div>
								<div className='info'>{etfpcf.nav}</div>
							</div>
						</div>

						{others.length ? (
							<div className='table-wrap others'>
								<table>
									<thead>
										<tr>
											<th>項目</th>
											<th>金額</th>
										</tr>
									</thead>

									<tbody>
										{others.map((other, index) => {
											return (
												<tr key={other.id + index}>
													<td>{other.cName}</td>
													<td>{other.amt}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</div>
						) : null}

						{stocks.length ? (
							<div className='stocks-section' ref={stocksSection}>
								<div className='section-title'>
									<div className='text'>股票</div>
									<div className='line'></div>
								</div>

								<div className='table-wrap'>
									<table>
										<thead>
											<tr>
												<th>股票代號</th>
												<th>股票名稱</th>
												<th>股數</th>
												<th>金額(元)</th>
												<th>佔基金淨資產之權重(%)</th>
											</tr>
										</thead>

										<tbody>
											{stocks.map((stock, index) => {
												return (
													<tr key={stock.id + String(index)}>
														<td>{stock.code}</td>
														<td>{stock.cName}</td>
														<td>{stock.qty}</td>
														<td>{stock.amt}</td>
														<td>{stock.weight}</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</div>
						) : null}

						{bonds.length ? (
							<div className='bond-characteristics-section'>
								<div className='section-title'>
									<div className='text'>持債特性</div>
									<div className='line'></div>
								</div>

								<div className='table-wrap'>
									<table>
										<thead>
											<tr>
												<th className='left'>項目</th>
												<th className='narrow'>{etfpcf.twName}</th>
												<th className='narrow'>{etfpcf.indexName}</th>
												<th className='right'>差異</th>
											</tr>
										</thead>

										<tbody>
											<tr>
												<td className='left'>成分債券檔數</td>
												<td>{etfpcf.bond_count}</td>
												<td>{etfpcf.index_count}</td>
												<td className='right'>{etfpcf.diff_count}</td>
											</tr>

											<tr>
												<td className='left'>持債比重(含息)(%)</td>
												<td>{etfpcf.bond_weight}</td>
												<td>{etfpcf.index_weight}</td>
												<td className='right'>{etfpcf.diff_weight}</td>
											</tr>

											<tr>
												<td className='left'>平均殖利率(%)</td>
												<td>{etfpcf.bond_yield}</td>
												<td>{etfpcf.index_yield}</td>
												<td className='right'>{etfpcf.diff_yield}</td>
											</tr>

											<tr>
												<td className='left'>平均票息率(%)</td>
												<td>{etfpcf.bond_coupon}</td>
												<td>{etfpcf.index_coupon}</td>
												<td className='right'>{etfpcf.diff_coupon}</td>
											</tr>

											<tr>
												<td className='left'>存續期間(年)</td>
												<td>{etfpcf.bond_duration}</td>
												<td>{etfpcf.index_duration}</td>
												<td className='right'>{etfpcf.diff_duration}</td>
											</tr>

											<tr>
												<td className='left'>到期年限(年)</td>
												<td>{etfpcf.bond_maturity}</td>
												<td>{etfpcf.index_maturity}</td>
												<td className='right'>{etfpcf.diff_maturity}</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						) : null}

						{bonds.length ? (
							<div className='bonds-section'>
								<div className='section-title'>
									<div className='text'>債券</div>
									<div className='line'></div>
								</div>

								<div className='table-wrap'>
									<table>
										<thead>
											<tr>
												<th>債券代號</th>
												<th>債券名稱</th>
												<th>面額</th>
												<th>市值</th>
												<th>持債比例(%)</th>
											</tr>
										</thead>

										<tbody>
											{bonds.map((bond, index) => {
												return (
													<tr key={bond.guid + index}>
														<td>{bond.code}</td>
														<td>{bond.cName}</td>
														<td>{bond.qty}</td>
														<td>{bond.amt}</td>
														<td>{bond.weight}</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</div>
						) : null}

						{futures.length ? (
							<div className='futures-section'>
								<div className='section-title'>
									<div className='text'>期貨</div>
									<div className='line'></div>
								</div>

								<div className='table-wrap'>
									<table>
										<thead>
											<tr>
												<th>期貨代號</th>
												<th>期貨名稱</th>
												<th>契約年月</th>
												<th>口數</th>
												<th>金額(元)</th>
												<th>持股權重(%)</th>
											</tr>
										</thead>

										<tbody>
											{futures.map((future, index) => {
												return (
													<tr key={future.id + index}>
														<td>{future.code}</td>
														<td>{future.cName}</td>
														<td>{future.dym}</td>
														<td>{future.qty}</td>
														<td>{future.amt}</td>
														<td>{future.weight}</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</div>
						) : null}

						<div className='notes'>
							<div className='flex-row'>備註 : </div>

							<div className='flex-row'>
								<div className='num'>(1)</div>
								<div className='text'>ETF系列基金之權重(%)為相對基金淨資產價值之比重，請注意權重比例可能因有在途款項等因素，而使比例總和超過100%。</div>
							</div>

							<div className='flex-row'>
								<div className='num'>(2)</div>
								<div className='text'>每現金申購或買回基數之實際申購總價金、申購總價金差額及買回總價金，以基金經理公司通知給付之金額為準。</div>
							</div>

							<div className='flex-row'>
								<div className='num'>(3)</div>
								<div className='text'>若因申購失敗或買回失敗而影響當日基金淨值時，每一申購基數申購總價金差額以及實際申購總價金，將以最新資料為準。</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ETFPCF;
